import { useState } from 'react';
import {
    validateEmail,
    validatePassword,
    validateStrongPassword,
    validateRequired,
    validateConfirmPassword
} from './validation.js';

/**
 * Generic form validation utility
 * @param {Object} data - The form data to validate
 * @param {Object} rules - Validation rules for each field
 * @returns {Object} - Object containing isValid boolean and errors object
 */
export const validateForm = (data, rules) => {
    const errors = {};

    Object.keys(rules).forEach(field => {
        const value = data[field];
        const fieldRules = rules[field];

        // Check each validation rule for the field
        fieldRules.forEach(rule => {
            if (errors[field]) return; // Skip if already has error

            const { validator, message } = rule;
            if (!validator(value, data)) {
                errors[field] = message;
            }
        });
    });

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Predefined validation rules for common forms
export const signInValidationRules = {
    email: [
        {
            validator: validateRequired,
            message: "Email is required"
        },
        {
            validator: validateEmail,
            message: "Please enter a valid email address"
        }
    ],
    password: [
        {
            validator: validateRequired,
            message: "Password is required"
        },
        {
            validator: validatePassword,
            message: "Password must be at least 6 characters long"
        }
    ]
};

export const signUpValidationRules = {
    email: [
        {
            validator: validateRequired,
            message: "Email is required"
        },
        {
            validator: validateEmail,
            message: "Please enter a valid email address"
        }
    ],
    password: [
        {
            validator: validateRequired,
            message: "Password is required"
        },
        {
            validator: validateStrongPassword,
            message: "Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters"
        }
    ],
    confirmPassword: [
        {
            validator: validateRequired,
            message: "Please confirm your password"
        },
        {
            validator: (confirmPassword, data) => validateConfirmPassword(data.password, confirmPassword),
            message: "Passwords do not match"
        }
    ]
};

export const forgotPasswordValidationRules = {
    email: [
        {
            validator: validateRequired,
            message: "Email is required"
        },
        {
            validator: validateEmail,
            message: "Please enter a valid email address"
        }
    ]
};

export const resetPasswordValidationRules = {
    newPassword: [
        {
            validator: validateRequired,
            message: "New password is required"
        },
        {
            validator: validatePassword,
            message: "Password must be at least 6 characters long"
        }
    ],
    confirmPassword: [
        {
            validator: validateRequired,
            message: "Please confirm your new password"
        },
        {
            validator: (confirmPassword, data) => validateConfirmPassword(data.newPassword, confirmPassword),
            message: "Passwords do not match"
        }
    ]
};

// Custom hook for form validation (optional)
export const useFormValidation = (initialData, validationRules) => {
    const [data, setData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [isValid, setIsValid] = useState(false);

    const validateField = (fieldName, value) => {
        const updatedData = { ...data, [fieldName]: value };
        const validation = validateForm(updatedData, validationRules);

        setData(updatedData);
        setErrors(validation.errors);
        setIsValid(validation.isValid);

        return validation;
    };

    const validateAllFields = () => {
        const validation = validateForm(data, validationRules);
        setErrors(validation.errors);
        setIsValid(validation.isValid);
        return validation;
    };

    const resetForm = () => {
        setData(initialData);
        setErrors({});
        setIsValid(false);
    };

    return {
        data,
        errors,
        isValid,
        validateField,
        validateAllFields,
        resetForm
    };
};
